"use client";

// The small shared pieces every room is built from.
//
//   useRoom      — one IntersectionObserver for the whole page. A room gets
//                  `data-in` once (its entrance plays, never replays) and
//                  `data-live` while it is on screen, so a room's little loops
//                  run only while someone can see them: nothing animates in a
//                  room you have scrolled past (law_snappy · the perf budget).
//   useCycle     — a step that advances on its own while the room is live,
//                  holds under a resting pointer, and gives way to any click.
//   Notice       — THE lifted callout (Figma components 25:225 "marketing/
//                  callout · the noticer, lifted"): a near-white capsule, netti's
//                  mark, one line of what just happened, a quiet time. It is the
//                  white overlay language the product already speaks.
//   RoomHead     — kicker · heading · dek, the one head every room wears.
//   Closer       — "look closer": the quiet link that opens the room's sheet.

import { useCallback, useEffect, useRef, useState } from "react";
import BrandIcon from "../../BrandIcon";

/* ── one observer, every room ─────────────────────────────────────────── */

type RoomSub = (live: boolean) => void;
const subs = new Map<Element, RoomSub>();
let io: IntersectionObserver | null = null;
function observer(): IntersectionObserver {
  if (io) return io;
  io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        const el = e.target as HTMLElement;
        if (e.isIntersecting) el.setAttribute("data-in", "1");
        if (e.isIntersecting) el.setAttribute("data-live", "1");
        else el.removeAttribute("data-live");
        subs.get(el)?.(e.isIntersecting);
      }
    },
    // a room is "live" while any meaningful part of it is on screen
    { threshold: 0.18, rootMargin: "0px 0px -8% 0px" },
  );
  return io;
}

/** observe a room: returns a ref callback and whether it is on screen now */
export function useRoom<T extends HTMLElement = HTMLElement>(): [(el: T | null) => void, boolean] {
  const [live, setLive] = useState(false);
  const elRef = useRef<T | null>(null);
  const ref = useCallback((el: T | null) => {
    const o = typeof window === "undefined" ? null : observer();
    if (elRef.current && o) {
      o.unobserve(elRef.current);
      subs.delete(elRef.current);
    }
    elRef.current = el;
    if (el && o) {
      subs.set(el, setLive);
      o.observe(el);
    }
  }, []);
  return [ref, live];
}

/** prefers-reduced-motion, read once on the client */
export function useReduced(): boolean {
  const [r, setR] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setR(mq.matches);
    const on = () => setR(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return r;
}

/** a step that walks itself while `live`, held while `held`, reset by a pick */
export function useCycle(n: number, live: boolean, ms: number, opts?: { start?: number; once?: boolean }) {
  const [i, setI] = useState(opts?.start ?? 0);
  const held = useRef(false);
  const touched = useRef(false);
  const reduced = useReduced();
  useEffect(() => {
    if (!live || reduced || n < 2) return;
    const t = window.setInterval(() => {
      if (held.current || touched.current) return;
      setI((v) => {
        if (opts?.once && v >= n - 1) return v;
        return (v + 1) % n;
      });
    }, ms);
    return () => window.clearInterval(t);
  }, [live, reduced, n, ms, opts?.once]);
  const pick = useCallback((v: number) => {
    touched.current = true;
    setI(v);
  }, []);
  const hold = {
    onPointerEnter: () => { held.current = true; },
    onPointerLeave: () => { held.current = false; },
  };
  return { i, pick, hold, setI };
}

/* ── the lifted callout ───────────────────────────────────────────────── */

export function Notice({
  children,
  meta,
  className,
  style,
  tone = "light",
}: {
  children: React.ReactNode;
  meta?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** "deep" is the ledger strip's night capsule (C 36:1044's middle line) */
  tone?: "light" | "deep";
}) {
  return (
    <span className={`hm-note${className ? ` ${className}` : ""}`} data-tone={tone} style={style}>
      <BrandIcon name="innernet" size={18} className="hm-note__mark" />
      <span className="hm-note__t">{children}</span>
      {meta && <span className="hm-note__m">{meta}</span>}
    </span>
  );
}

/* ── the room head ────────────────────────────────────────────────────── */

export function RoomHead({
  n,
  kicker,
  title,
  dek,
  align = "left",
  id,
}: {
  n?: string;
  kicker: string;
  title: React.ReactNode;
  dek?: React.ReactNode;
  align?: "left" | "center";
  id?: string;
}) {
  return (
    <header className="hm-head" data-align={align}>
      <p className="hm-kick hm-up" style={{ "--d": 0 } as React.CSSProperties}>
        {n && <span className="hm-kick__n">{n}</span>}
        {n && <span aria-hidden> · </span>}
        {kicker}
      </p>
      <h2 id={id} className="hm-h hm-up" style={{ "--d": 1 } as React.CSSProperties}>{title}</h2>
      {dek && <p className="hm-dek hm-up" style={{ "--d": 2 } as React.CSSProperties}>{dek}</p>}
    </header>
  );
}

/* ── look closer ──────────────────────────────────────────────────────── */

export function Closer({ onOpen, label = "look closer" }: { onOpen: () => void; label?: string }) {
  return (
    <button type="button" className="hm-closer quiet-link" onClick={onOpen}>
      {label}
      <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden>
        <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

/** a copy that confirms itself: returns [done, copy] */
export function useCopy(text: string): [boolean, () => void] {
  const [done, setDone] = useState(false);
  const copy = useCallback(() => {
    try {
      void navigator.clipboard?.writeText(text);
    } catch {
      /* the text is on screen and selectable; that fallback always works */
    }
    setDone(true);
    window.setTimeout(() => setDone(false), 1800);
  }, [text]);
  return [done, copy];
}
